/**
 * CSS Class Typeahead Component for Alpine.js
 *
 * Enhances the page editor's "CSS Classes" field (a plain space-separated text
 * input, id `page_css_classes`) with a suggestion dropdown sourced from the
 * classes parsed out of the page stylesheets (WpsPages::CssClasses). The full
 * list is embedded in a data attribute, so filtering is entirely client-side —
 * no network request. Users are never limited to the list: anything they type
 * stays in the input; suggestions only match the word currently being typed.
 */

export function cssClassTypeahead(allClasses = []) {
  return {
    // State
    allClasses: Array.isArray(allClasses) ? allClasses : [],
    value: '',
    suggestions: [],
    isOpen: false,
    selectedIndex: -1,

    init() {
      // Seed from the server-rendered input value (round-trips on edit).
      const input = this.$el.querySelector('input[type="text"]');
      if (input) this.value = input.value;

      // Click outside closes the dropdown.
      this.$el.addEventListener('click', (e) => e.stopPropagation());
      document.addEventListener('click', () => {
        this.isOpen = false;
      });
    },

    // Tokens already present in the value (so we don't re-suggest them).
    tokens() {
      return this.value.split(/\s+/).filter(Boolean);
    },

    // The partial class the caret is currently typing (text after last space).
    currentWord() {
      const parts = this.value.split(/\s+/);
      return parts[parts.length - 1] || '';
    },

    onInput() {
      const word = this.currentWord().toLowerCase();
      const existing = this.tokens();

      if (!word) {
        this.suggestions = [];
        this.isOpen = false;
        this.selectedIndex = -1;
        return;
      }

      this.suggestions = this.allClasses
        .filter(cls => cls.toLowerCase().includes(word))
        .filter(cls => !existing.includes(cls) || cls.toLowerCase() === word)
        .slice(0, 25);

      this.isOpen = this.suggestions.length > 0;
      this.selectedIndex = -1;
    },

    selectSuggestion(cls) {
      // Replace the trailing partial word with the chosen class + a space.
      const parts = this.value.split(/\s+/);
      parts[parts.length - 1] = cls;
      this.value = parts.join(' ') + ' ';

      this.suggestions = [];
      this.isOpen = false;
      this.selectedIndex = -1;

      // Sync the underlying input + notify any listeners (dirty tracking).
      const input = this.$el.querySelector('input[type="text"]');
      if (input) {
        input.value = this.value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      }
    },

    onKeydown(event) {
      if (!this.isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          break;

        case 'Enter':
          if (this.selectedIndex >= 0 && this.selectedIndex < this.suggestions.length) {
            event.preventDefault();
            this.selectSuggestion(this.suggestions[this.selectedIndex]);
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.isOpen = false;
          this.selectedIndex = -1;
          break;
      }
    },

    getSuggestionClass(index) {
      const baseClasses = 'px-3 py-2 cursor-pointer';
      const hoverClasses = 'hover:bg-gray-100';
      const selectedClasses = this.selectedIndex === index ? 'bg-blue-50 text-blue-700' : '';
      return `${baseClasses} ${hoverClasses} ${selectedClasses}`.trim();
    }
  };
}

// Make available globally for Alpine
window.cssClassTypeahead = cssClassTypeahead;
